<template>
  <!-- Coverage Report Dialog -->
  <BaseDialog
    v-model="showCoverageDialog"
    title="Coverage Report"
    :max-width="900"
    toolbar-color="indigo"
    :show-save="false"
    @save="closeCoverageDialog"
  >
    <!-- Date Range Selection -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="coverageStartDate"
          label="Start Date"
          type="date"
          :max="coverageEndDate"
          density="comfortable"
          variant="outlined"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="coverageEndDate"
          label="End Date"
          type="date"
          :min="coverageStartDate"
          :max="currentDate"
          density="comfortable"
          variant="outlined"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-btn
          color="primary"
          variant="flat"
          @click="fetchCoverage"
          :loading="coverageLoading"
          block
          height="40"
        >
          <v-icon start>mdi-check</v-icon>
          Check Coverage
        </v-btn>
      </v-col>
    </v-row>

    <!-- Quick Date Buttons -->
    <div class="mb-4">
      <v-chip
        v-for="option in quickDateOptions"
        :key="option.label"
        size="small"
        class="mr-2 mb-2"
        @click="selectQuickDate(option)"
        :color="isActiveQuickDate(option) ? 'primary' : 'default'"
        variant="outlined"
      >
        {{ option.label }}
      </v-chip>
    </div>

    <!-- Loading State -->
    <div v-if="coverageLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-grey">Loading coverage data...</div>
    </div>

    <!-- Error State -->
    <v-alert v-else-if="coverageError" type="error" variant="outlined" class="mb-4">
      {{ coverageError }}
    </v-alert>

    <!-- Results -->
    <div v-else-if="coverageData">
      <!-- Summary Card -->
      <v-card variant="outlined" class="mb-4">
        <v-card-text class="pa-3">
          <v-row>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold">{{ coverageData.summary.totalDays }}</div>
              <div class="text-caption text-grey">Total Days</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold text-success">
                {{ coverageData.summary.completeDays }}
              </div>
              <div class="text-caption text-grey">Complete</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold text-warning">
                {{ coverageData.summary.incompleteDays }}
              </div>
              <div class="text-caption text-grey">Incomplete</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ coverageData.summary.averageCoverage }}%
              </div>
              <div class="text-caption text-grey">Avg Coverage</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Daily Coverage Table -->
      <v-data-table-virtual
        :headers="tableHeaders"
        :items="coverageData.days"
        density="compact"
        class="elevation-1"
      >
        <template v-slot:item.date="{ item }">
          <span class="font-weight-medium">{{ formatLocalDate(item.date) }}</span>
          <span class="text-caption text-grey ml-2">{{ item.dayOfWeek }}</span>
        </template>

        <template v-slot:item.isComplete="{ item }">
          <v-icon
            :color="item.isComplete ? 'success' : item.reported === 0 ? 'grey' : 'warning'"
            size="small"
          >
            {{
              item.isComplete
                ? "mdi-check-circle"
                : item.reported === 0
                  ? "mdi-circle-outline"
                  : "mdi-alert-circle"
            }}
          </v-icon>
        </template>

        <template v-slot:item.coverage="{ item }">
          <span>{{ item.reported }} / {{ item.expected }}</span>
        </template>

        <template v-slot:item.percentage="{ item }">
          <v-chip :color="getPercentageColor(item.percentage)" size="x-small" variant="flat">
            {{ item.percentage }}%
          </v-chip>
        </template>

        <template v-slot:item.missing="{ item }">
          <span v-if="item.missing > 0" class="text-warning">{{ item.missing }} missing</span>
          <span v-else class="text-success">-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.missing > 0 && item.missingCombinations"
            icon
            size="x-small"
            variant="text"
            color="info"
            @click="showMissingDetails(item)"
          >
            <v-icon>mdi-information-outline</v-icon>
            <v-tooltip activator="parent" location="top">View missing combinations</v-tooltip>
          </v-btn>
        </template>
      </v-data-table-virtual>
    </div>

    <!-- Initial State -->
    <div v-else class="text-center py-8 text-grey">
      <v-icon size="64" color="grey-lighten-1">mdi-chart-box-outline</v-icon>
      <div class="mt-2">Select a date range and click "Check Coverage"</div>
    </div>

    <!-- Extra action buttons: PDF export -->
    <template #actions>
      <v-btn variant="flat" color="indigo" @click="exportPdf" :disabled="!coverageData">
        <v-icon start>mdi-file-pdf-box</v-icon>
        PDF
      </v-btn>
    </template>
  </BaseDialog>

  <!-- Coverage Details Dialog -->
  <BaseDialog
    v-model="showMissingDialog"
    :title="selectedDay ? 'Coverage Details — ' + formatLocalDate(selectedDay.date) + ' (' + selectedDay.dayOfWeek + ')' : 'Coverage Details'"
    :max-width="600"
    :show-save="false"
    wrap-title
  >
    <template #default>
      <v-tabs v-model="detailsTab" bg-color="grey-lighten-4" class="mx-n4 mt-n4 mb-4">
        <v-tab value="missing">
          <v-icon start size="small">mdi-alert-circle</v-icon>
          Missing
          <v-chip size="x-small" color="warning" variant="flat" class="ml-2">
            {{ missingCombinations.length }}
          </v-chip>
        </v-tab>
        <v-tab value="reported">
          <v-icon start size="small">mdi-check-circle</v-icon>
          Reported
          <v-chip size="x-small" color="success" variant="flat" class="ml-2">
            {{ reportedCombinations.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-data-table
        :headers="detailsTableHeaders"
        :items="detailsTab === 'missing' ? missingCombinations : reportedCombinations"
        :sort-by="[{ key: 'platform', order: 'asc' }]"
        density="compact"
        class="elevation-1"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:no-data>
          <div class="text-center text-grey py-4">
            {{ detailsTab === 'missing' ? 'No missing combinations' : 'No reported combinations' }}
          </div>
        </template>
      </v-data-table>
    </template>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import BaseDialog from "@/components/BaseDialog.vue";
import { useSalesMenu } from "@/composables/useSalesMenu.js";
import { generateCoveragePdfReport } from "@/utils/coveragePdfReport.js";

// Details dialog tab state
const detailsTab = ref("missing");

const {
  showCoverageDialog,
  coverageLoading,
  coverageError,
  coverageData,
  coverageStartDate,
  coverageEndDate,
  selectedDay,
  showMissingDialog,
  currentDate,
  closeCoverageDialog,
  fetchCoverage,
  showMissingDetails,
  closeMissingDialog,
  getDateNDaysAgo,
} = useSalesMenu();

// Auto-set end date when start date is entered
watch(coverageStartDate, (newVal) => {
  if (newVal && !coverageEndDate.value) {
    coverageEndDate.value = newVal;
  }
});

// Reset tab to "missing" when dialog opens
watch(showMissingDialog, (newVal) => {
  if (newVal) {
    detailsTab.value = "missing";
  }
});

// Computed: filter combinations by missing/reported
const allCombinations = computed(() => {
  return selectedDay.value?.allCombinations || selectedDay.value?.missingCombinations || [];
});

const missingCombinations = computed(() => {
  return allCombinations.value.filter((c) => c.isMissing);
});

const reportedCombinations = computed(() => {
  return allCombinations.value.filter((c) => !c.isMissing);
});

// Details table headers
const detailsTableHeaders = [
  { title: "Platform", key: "platform", sortable: true },
  { title: "City", key: "city", sortable: true },
  { title: "Location", key: "location", sortable: true },
  { title: "Brand", key: "brand", sortable: true },
];

// Table headers
const tableHeaders = [
  { title: "Date", key: "date", sortable: true },
  { title: "Status", key: "isComplete", sortable: true, align: "center" },
  { title: "Coverage %", key: "percentage", sortable: true, align: "center" },
  { title: "Coverage", key: "coverage", sortable: false },
  { title: "Missing", key: "missing", sortable: true },
  { title: "", key: "actions", sortable: false, width: "50px" },
];

// Quick date options
const quickDateOptions = computed(() => [
  { label: "Today", start: currentDate, end: currentDate },
  { label: "Yesterday", start: getDateNDaysAgo(1), end: getDateNDaysAgo(1) },
  { label: "Last 7 Days", start: getDateNDaysAgo(7), end: currentDate },
  { label: "Last 14 Days", start: getDateNDaysAgo(14), end: currentDate },
  { label: "Last 30 Days", start: getDateNDaysAgo(30), end: currentDate },
]);

function isActiveQuickDate(option) {
  return option.start === coverageStartDate.value && option.end === coverageEndDate.value;
}

function selectQuickDate(option) {
  coverageStartDate.value = option.start;
  coverageEndDate.value = option.end;
}

function getPercentageColor(percentage) {
  if (percentage === 100) return "success";
  if (percentage >= 80) return "warning";
  if (percentage > 0) return "error";
  return "grey";
}

function exportPdf() {
  generateCoveragePdfReport(coverageData.value);
}

function formatLocalDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
</script>

<style scoped>
.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.bg-warning-lighten-4 {
  background-color: rgb(var(--v-theme-warning), 0.15) !important;
}

</style>
